/**
 * Card allegati riutilizzabile (template e newsletter) — §8.5 / §11.6.
 * Presentazionale: lo stato (esistenti/staged) e le chiamate API sono gestite dal parent.
 *
 * Tre gruppi di allegati:
 *  - inherited: ereditati (es. dal template in composizione) → sola lettura, chip "Dal template".
 *  - existing:  già caricati sull'oggetto → rimozione immediata (onRemoveExisting).
 *  - staged:    aggiunti ma non ancora caricati → chip "Da caricare", rimozione locale (onRemoveStaged).
 *
 * In modalità `disabled` la card è in sola lettura (niente aggiungi/rimuovi).
 */
import { useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Card, CardContent, Stack, Typography, Divider, Chip, IconButton, Tooltip, CircularProgress } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { DefaultOutlinedButton } from '../../../../utils/Buttons';
import { ATTACH_ACCEPT, attachmentName, formatFileSize, partitionAttachments } from '../../../../utils/newsletter/attachments';

function Row({ icon, primary, secondary, chip, action }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 1 }}>
      {icon}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap>
          {primary}
        </Typography>
        {secondary && (
          <Typography variant="caption" sx={{ color: '#919EAB' }}>
            {secondary}
          </Typography>
        )}
      </Box>
      {chip}
      {action}
    </Stack>
  );
}

Row.propTypes = {
  icon: PropTypes.node,
  primary: PropTypes.node,
  secondary: PropTypes.node,
  chip: PropTypes.node,
  action: PropTypes.node,
};

/**
 * @param {{
 *   title?: string,
 *   helperText?: string,
 *   inherited?: Array,
 *   existing?: Array,
 *   staged?: File[],
 *   onFilesPicked?: (result: { valid: File[], rejected: string[] }) => void,
 *   onRemoveStaged?: (index: number) => void,
 *   onRemoveExisting?: (att: object) => void,
 *   removingAttId?: string|null,
 *   disabled?: boolean,
 * }} props
 */
export function AttachmentsCard({
  title = 'Allegati',
  helperText = 'PDF, immagini o documenti Office. Max 10MB per file.',
  inherited = [],
  existing = [],
  staged = [],
  onFilesPicked,
  onRemoveStaged,
  onRemoveExisting,
  removingAttId = null,
  disabled = false,
  onPickFromLibrary,
  library = [],
  onRemoveLibrary,
}) {
  const inputRef = useRef(null);

  const handleInput = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = ''; // consente di re-selezionare lo stesso file
    onFilesPicked?.(partitionAttachments(picked));
  };

  const isEmpty = inherited.length === 0 && existing.length === 0 && staged.length === 0 && library.length === 0;
  const fileIcon = <InsertDriveFileOutlinedIcon fontSize="small" sx={{ color: '#637381' }} />;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="subtitle2">{title}</Typography>
              <Typography variant="caption" sx={{ color: '#637381' }}>
                {helperText}
              </Typography>
            </Box>
            {!disabled && (
              <Stack direction="row" spacing={1}>
                {/* Libreria: evita di ricaricare un file già presente. */}
                {onPickFromLibrary && (
                  <DefaultOutlinedButton startIcon={<PhotoLibraryIcon />} onClick={onPickFromLibrary}>
                    Dalla libreria
                  </DefaultOutlinedButton>
                )}
                <DefaultOutlinedButton startIcon={<AttachFileIcon />} onClick={() => inputRef.current?.click()}>
                  Aggiungi allegato
                </DefaultOutlinedButton>
                <input ref={inputRef} type="file" multiple hidden accept={ATTACH_ACCEPT} onChange={handleInput} />
              </Stack>
            )}
          </Stack>

          {isEmpty ? (
            <Typography variant="body2" sx={{ color: '#919EAB' }}>
              Nessun allegato.
            </Typography>
          ) : (
            <Stack divider={<Divider flexItem />} spacing={0}>
              {/* Ereditati dal template (sola lettura) */}
              {inherited.map((att) => (
                <Row
                  key={`inherited-${att.id}`}
                  icon={fileIcon}
                  primary={attachmentName(att)}
                  secondary={att.size != null ? formatFileSize(att.size) : undefined}
                  chip={<Chip size="small" label="Dal template" variant="outlined" sx={{ mr: 0.5 }} />}
                />
              ))}

              {/* Allegati già caricati */}
              {existing.map((att) => (
                <Row
                  key={att.id}
                  icon={fileIcon}
                  primary={attachmentName(att)}
                  secondary={att.size != null ? formatFileSize(att.size) : undefined}
                  action={
                    !disabled && (
                      <Tooltip title="Rimuovi" placement="top" arrow>
                        <span>
                          <IconButton
                            size="small"
                            disabled={removingAttId === att.id}
                            onClick={() => onRemoveExisting?.(att)}
                          >
                            {removingAttId === att.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DeleteOutlineIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )
                  }
                />
              ))}

              {/* File aggiunti, non ancora caricati */}
              {/* Scelti dalla libreria: il file è già su S3, si crea solo il collegamento. */}
              {library.map((media, index) => (
                <Row
                  key={`library-${media.id}`}
                  icon={fileIcon}
                  primary={media.file_name}
                  secondary={formatFileSize(media.size)}
                  chip={<Chip size="small" label="Dalla libreria" sx={{ mr: 0.5 }} />}
                  action={
                    !disabled && (
                      <Tooltip title="Rimuovi" placement="top" arrow>
                        <span>
                          <IconButton size="small" onClick={() => onRemoveLibrary?.(index)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )
                  }
                />
              ))}

              {staged.map((file, index) => (
                <Row
                  // eslint-disable-next-line react/no-array-index-key
                  key={`staged-${index}`}
                  icon={fileIcon}
                  primary={file.name}
                  secondary={formatFileSize(file.size)}
                  chip={<Chip size="small" label="Da caricare" sx={{ mr: 0.5 }} />}
                  action={
                    !disabled && (
                      <Tooltip title="Rimuovi" placement="top" arrow>
                        <span>
                          <IconButton size="small" onClick={() => onRemoveStaged?.(index)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )
                  }
                />
              ))}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

AttachmentsCard.propTypes = {
  title: PropTypes.string,
  helperText: PropTypes.string,
  inherited: PropTypes.array,
  existing: PropTypes.array,
  staged: PropTypes.array,
  onFilesPicked: PropTypes.func,
  onRemoveStaged: PropTypes.func,
  onRemoveExisting: PropTypes.func,
  removingAttId: PropTypes.string,
  disabled: PropTypes.bool,
  // Se assente, il pulsante "Dalla libreria" non compare.
  onPickFromLibrary: PropTypes.func,
  // File scelti dalla libreria, non ancora collegati alla newsletter.
  library: PropTypes.array,
  onRemoveLibrary: PropTypes.func,
};

export default AttachmentsCard;
